// Pricing migration 32 (ADO-3985), T14 to T17 and T19: the multi-buy parts of the side cart and
// the order review.
//   CartDealStrip   a side cart line's compact Offer Strip (A4, 14, R2 12w, R1 12s, R3 21w,
//                   21s): the gift, the words and an arrow, the whole strip one button that opens
//                   the deal (in the panel, 15; never a dialog on top of it).
//   ReviewDealPill  an order review line's pill (R2 17w "Multi-buy · +1 free case", Tone=Success;
//                   R1 17s "Multi-buy · 4 of 4" with BWG's note under it, red).
//   CartDealFlag    the incomplete-deal flag, Banner Type=Offer (14, 16, 19), with ✕.
// The strip is named by its words plus the deal it opens (T17): "Add 1 more · any of 3 lines, see
// the Buy 4, get 1 free deal".

import type { ReactNode } from 'react';
import { Banner, Icon } from '../design-system';
import type { CartLineStrip } from './cartDeals';
import { dealOfferText } from './multibuyDealText';
import styles from './CartDealParts.module.css';

export function CartDealStrip({
  strip,
  onOpen,
}: {
  readonly strip: CartLineStrip;
  readonly onOpen: () => void;
}) {
  if (strip.deal === null) {
    // A WOW free count whose deal is not read: the words stay, with no arrow and nothing to open.
    return (
      <p className={styles.strip} data-multibuy-line-strip={strip.kind}>
        <Icon name="gift" size={14} />
        <span className={styles.stripText}>{strip.text}</span>
      </p>
    );
  }
  return (
    <button
      type="button"
      className={styles.strip}
      data-multibuy-line-strip={strip.kind}
      aria-label={`${strip.text}, see the ${dealOfferText(strip.deal)} deal`}
      onClick={onOpen}
    >
      <Icon name="gift" size={14} />
      <span className={styles.stripText}>{strip.text}</span>
      <Icon name="arrowRight" size={14} />
    </button>
  );
}

export function ReviewDealPill({
  text,
  note,
  tone,
}: {
  readonly text: string;
  readonly note: string | null;
  /** `success`: the WOW free count (R2 17w, Tone=Success); `offer`: a standard entry's reached
   *  deal (R1 17s, the Promo pill relabelled). */
  readonly tone: 'success' | 'offer';
}) {
  return (
    <>
      <span className={styles.pill} data-multibuy-pill="" data-tone={tone}>
        <Icon name="tag" size={12} />
        {text}
      </span>
      {note === null ? null : <span className={styles.note}>{note}</span>}
    </>
  );
}

export function CartDealFlag({
  title,
  body,
  action,
  onDismiss,
  className,
}: {
  readonly title: string;
  readonly body: string;
  /** "See qualifying lines →" or "Show me ↓". */
  readonly action: ReactNode;
  readonly onDismiss: () => void;
  readonly className?: string | undefined;
}) {
  return (
    <Banner
      type="offer"
      title={title}
      onDismiss={onDismiss}
      {...(className === undefined ? {} : { className })}
    >
      <span className={styles.flagBody}>{body}</span>
      {action}
    </Banner>
  );
}

/** The flag's text link (14: "See qualifying lines →"; 16, 19: "Show me ↓"). */
export function CartDealFlagLink({
  label,
  icon,
  onClick,
}: {
  readonly label: string;
  readonly icon: 'arrowRight' | 'arrowDown';
  readonly onClick: () => void;
}) {
  return (
    <button type="button" className={styles.flagLink} onClick={onClick}>
      {label}
      <Icon name={icon} size={14} />
    </button>
  );
}
